import { useMutation, useQueryClient } from "@tanstack/react-query";

import { fetchClient, invalidate } from "./client";
import type { components } from "./schema";

export type PlanningConfig = components["schemas"]["PlanningConfig"];
type PatchedPlanningConfig =
  components["schemas"]["PatchedPlanningConfigRequest"];
export type BulkAssignRequest = components["schemas"]["BulkAssignRequest"];

/** Forecasts, stock projections, policy member counts and the items list all
 *  read planning configs. */
const invalidateConfigQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) =>
  invalidate(
    queryClient,
    "/api/v1/demand/",
    "/api/v1/stock/projection/",
    "/api/v1/planning-runs/{id}/projection/",
    "/api/v1/planning-policies/",
    "/api/v1/items/",
  );

/** Upserts the policy on node rows. */
export function useBulkAssignPlanningPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkAssignRequest) =>
      fetchClient.POST("/api/v1/planning-configs/bulk-assign/", { body }),
    onSuccess: () => invalidateConfigQueries(queryClient),
  });
}

/**
 * PATCH /api/v1/planning-configs/{item}/{location}/ — upsert; the row is
 * created on first write, so callers never check whether one exists.
 *
 * Stored fields are the source of truth; `method` is always concrete and
 * `planningPolicy` is the FK that gates worklist inclusion (NULL = forecast only).
 */
export function useUpdatePlanningConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      item,
      location,
      ...body
    }: { item: number; location: number } & PatchedPlanningConfig) =>
      (
        await fetchClient.PATCH("/api/v1/planning-configs/{item}/{location}/", {
          params: { path: { item, location } },
          body,
        })
      ).data!,
    onSuccess: () => invalidateConfigQueries(queryClient),
  });
}
